var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};

// src/micro-frontend.tsx
import React, { useRef as useRef2 } from "react";

// src/use-micro-frontend.tsx
import { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";

// src/is-browser.ts
function isBrowser() {
  return typeof document !== "undefined";
}

// src/utils.ts
function tryJsonParse(data) {
  try {
    return JSON.parse(data);
  } catch {
  }
  return null;
}
function validateModule(mf) {
  if (!mf) {
    throw new Error("Micro-frontend module is not defined.");
  }
  if (typeof mf.mount !== "function") {
    throw new Error("Micro-frontend mount function is not defined.");
  }
  if (typeof mf.unmount !== "function") {
    throw new Error("Micro-frontend unmount function is not defined.");
  }
}
var mfDataKey = Symbol("mfData");
if (isBrowser()) {
  window[mfDataKey] = {};
}
function getFromCache(id) {
  return window[mfDataKey][id] ?? {};
}
function setIntoCache(id, data) {
  window[mfDataKey][id] = __spreadValues(__spreadValues({}, getFromCache(id)), data);
}
function markAsHydrated(id) {
  setIntoCache(id, { isHydrated: true });
}
function getMfDomData(id) {
  const html = "";
  const version = null;
  const inputData = {};
  if (!isBrowser()) {
    return { html, inputData, version };
  }
  const scriptEl = document.querySelector(`[data-mf-id="${id}"][data-mf-role="data"]`);
  if (scriptEl) {
    setIntoCache(id, {
      inputData: tryJsonParse(scriptEl.innerHTML) ?? inputData,
      version: scriptEl.getAttribute("data-mf-version") ?? version
    });
  }
  const placeholderEl = document.querySelector(`[data-mf-id="${id}"][data-mf-role="placeholder"]`);
  setIntoCache(id, {
    html: (placeholderEl == null ? void 0 : placeholderEl.innerHTML) ?? html
  });
  return getFromCache(id);
}
function getGlobalThis() {
  if (typeof globalThis !== "undefined") {
    return globalThis;
  }
  if (typeof window !== "undefined") {
    return window;
  }
  if (typeof self !== "undefined") {
    return self;
  }
  if (typeof global !== "undefined") {
    return global;
  }
  throw new Error("unable to locate global object");
}

// src/use-micro-frontend.tsx
function useMicroFrontend({
  loader,
  el,
  id,
  originalInputData
}) {
  const [state, setState] = useState({
    mfState: 0 /* loading */,
    error: null,
    mf: null
  });
  const { mf, mfState, error } = state;
  const { inputData, html, version } = getMfDomData(id);
  const useIsomorphicLayoutEffect = isBrowser() ? useLayoutEffect : useEffect;
  const ssrData = useRef(inputData);
  const mergedData = useMemo(() => __spreadValues(__spreadValues({}, ssrData.current), originalInputData), [originalInputData, ssrData]);
  useEffect(() => {
    async function load() {
      try {
        const mf2 = await loader({ version });
        validateModule(mf2);
        setState({
          mf: mf2,
          mfState: 1 /* loaded */,
          error: null
        });
      } catch (err) {
        const error2 = err instanceof Error ? err : new Error(String(err));
        setState({ error: error2, mf: null, mfState: 4 /* error */ });
      }
    }
    load();
  }, []);
  useIsomorphicLayoutEffect(() => {
    async function mount() {
      try {
        await (mf == null ? void 0 : mf.mount(mergedData, el));
        setState({ mf, error: null, mfState: 2 /* mounted */ });
      } catch (error2) {
        setState({ mf, error: error2, mfState: 4 /* error */ });
      }
      markAsHydrated(id);
    }
    if (mfState === 1 /* loaded */) {
      mount();
    }
    return () => {
      if (mfState === 2 /* mounted */) {
        mf == null ? void 0 : mf.unmount(el);
      }
    };
  }, [mf, mfState, id]);
  useEffect(() => {
    async function update() {
      var _a;
      try {
        await ((_a = mf == null ? void 0 : mf.update) == null ? void 0 : _a.call(mf, mergedData, el));
      } catch (error2) {
        setState({ mf, error: error2, mfState: 4 /* error */ });
      }
    }
    if (mfState === 2 /* mounted */) {
      update();
    }
  }, [mergedData]);
  if (error) {
    throw error;
  }
  return {
    showLoading: html === "" && mfState !== 2 /* mounted */,
    inputData: mergedData,
    version
  };
}

// src/micro-frontend.tsx
import { safeStringify } from "@glass-mf/utils";
var isRequiredProp = (prop) => {
  throw new Error(`${prop} property is required`);
};
var MicroFrontendPlaceholder = React.memo(React.forwardRef(function MicroFrontendPlaceholder2({ id, ssrEndpoint, css }, ref) {
  return /* @__PURE__ */ React.createElement("div", {
    "data-mf-id": id,
    "data-mf-role": "placeholder",
    "data-mf-url": ssrEndpoint,
    "data-mf-css": css,
    ref,
    suppressHydrationWarning: true,
    dangerouslySetInnerHTML: { __html: "" }
  });
}), () => true);
function MicroFrontend({
  id = isRequiredProp("id"),
  loader = isRequiredProp("loader"),
  css,
  ssrEndpoint,
  inputData: originalInputData,
  children
}) {
  const ref = useRef2(null);
  const { showLoading, inputData, version } = useMicroFrontend({
    loader,
    el: ref.current,
    id,
    originalInputData
  });
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("script", {
    "data-mf-id": id,
    "data-mf-role": "data",
    type: "application/json",
    "data-mf-version": version,
    dangerouslySetInnerHTML: {
      __html: inputData ? safeStringify(JSON.stringify(inputData)) : ""
    }
  }), /* @__PURE__ */ React.createElement(MicroFrontendPlaceholder, {
    ref,
    id,
    ssrEndpoint,
    css
  }), showLoading ? /* @__PURE__ */ React.createElement("div", {
    "data-mf-id": id,
    "data-mf-role": "loader"
  }, children) : null);
}

// src/loaders/simple-loader.ts
import { getAssetSrc } from "@glass-mf/utils";
async function isStyleSheetLoaded(el) {
  var _a, _b;
  if (!el) {
    return false;
  }
  try {
    if ((_b = (_a = el.sheet) == null ? void 0 : _a.cssRules) == null ? void 0 : _b.length) {
      return true;
    }
    return new Promise((resolve) => {
      el.onload = () => resolve(true);
      el.onerror = () => resolve(false);
      setTimeout(() => resolve(false), 1e3);
    });
  } catch (_) {
    return false;
  }
}
var SimpleLoader = class {
  registry = {};
  loadingPromises = {};
  register(manifest) {
    if (!manifest.name) {
      throw new Error("Manifest name not defined.");
    }
    this.registry[manifest.name] = manifest;
  }
  async get(name, version) {
    const manifest = this.registry[name];
    if (!manifest) {
      throw new Error(`Manifest ${name} not defined.`);
    }
    const mfName = manifest.name;
    if (manifest.style) {
      const style = getAssetSrc(manifest.style, version);
      await this.loadStyle(mfName, style);
    }
    const src = getAssetSrc(manifest.script, version);
    return await this.loadScript(mfName, src);
  }
  async loadModule(path) {
    return import(
      /* @vite-ignore */
      /* webpackIgnore: true */
      path
    );
  }
  async loadScript(name, src) {
    const win = getGlobalThis();
    return this.loadModule(src).then((mod) => {
      const globalMfModule = win[name];
      return globalMfModule ?? mod;
    });
  }
  async loadStyle(name, src) {
    const isExistingLinkLoaded = await isStyleSheetLoaded(document.querySelector(`link[data-name=${name}]`));
    if (isExistingLinkLoaded) {
      return;
    }
    if (typeof this.loadingPromises[src] === "undefined") {
      this.loadingPromises[src] = new Promise((resolve) => {
        const linkEl = document.createElement("link");
        linkEl.href = src;
        linkEl.rel = "stylesheet";
        linkEl.onload = resolve;
        linkEl.onerror = () => resolve();
        linkEl.dataset.name = name;
        const el = document.getElementsByTagName("head")[0] || document.documentElement;
        el.appendChild(linkEl);
      });
    }
    return this.loadingPromises[src];
  }
};

// src/loaders/module-federation-loader.ts
var getContainer = (name) => window[name];
async function getModule(name, module) {
  await __webpack_init_sharing__("default");
  const container = getContainer(name);
  await container.init(__webpack_share_scopes__.default);
  const factory = await container.get(module);
  return factory();
}
var injectScript = ({ url }) => {
  return new Promise((resolve, reject) => {
    const element = document.createElement("script");
    element.src = url;
    element.async = true;
    element.type = "text/javascript";
    element.onload = () => {
      resolve(null);
      document.head.removeChild(element);
    };
    element.onerror = () => {
      document.head.removeChild(element);
      reject(new Error(`Script Error: ${url}`));
    };
    document.head.appendChild(element);
  });
};
async function loadModuleScript(name, url) {
  const container = getContainer(name);
  if (typeof container !== "undefined") {
    return null;
  }
  await injectScript({
    url
  });
}
async function loadMf({
  app,
  url,
  module
}) {
  await loadModuleScript(app, url);
  const microFrontend = await getModule(app, module);
  return microFrontend;
}

// src/create-react-micro-frontend.ts
import React3 from "react";
import ReactDOM2 from "react-dom";

// src/create-react18-micro-frontend.ts
import React2 from "react";
import ReactDOM from "react-dom";
var validateRoot = (root) => {
  if (!root) {
    throw new Error("Root has not been mounted");
  }
};
function createReact18MicroFrontend(component) {
  let root;
  return {
    mount: async function(props = {}, element) {
      root = ReactDOM.createRoot(element);
      root.render(React2.createElement(component, __spreadValues({}, props)));
    },
    unmount: async function() {
      validateRoot(root);
      root.unmount();
    },
    update: async function(props = {}) {
      validateRoot(root);
      root.render(React2.createElement(component, __spreadValues({}, props)));
    }
  };
}

// src/create-react-micro-frontend.ts
function createReactMicroFrontendLegacy(component) {
  return {
    mount: async function(props = {}, element) {
      return new Promise((res) => {
        ReactDOM2.render(React3.createElement(component, __spreadValues({}, props)), element, res);
      });
    },
    unmount: async function(element) {
      ReactDOM2.unmountComponentAtNode(element);
    },
    update: async function(props = {}, element) {
      return new Promise((resolve) => {
        ReactDOM2.render(React3.createElement(component, __spreadValues({}, props)), element, resolve);
      });
    }
  };
}
function createReactMicroFrontend(component) {
  if (parseInt(React3.version) >= 18) {
    return createReact18MicroFrontend(component);
  }
  return createReactMicroFrontendLegacy(component);
}
export {
  MicroFrontend,
  SimpleLoader,
  createReactMicroFrontend,
  loadMf as moduleFederationLoader
};
//# sourceMappingURL=index.js.map